import { CalculatedOdds, Odds, emptyCalculatedOdds, emptyOdds } from '@frontend/sports/odds/feature';
import { Decimal } from 'decimal.js';

import { IBet, ISlip } from '../bet-generation/models';
import { SingleSlipType } from '../bet-generation/slip-type';
import { ITaxationResult } from '../taxation/models';

export interface IBetSummary {
    bet: IBet;
    possibleWinnings: Decimal | null;
    freeBetWinnings: Decimal | null;
}

export interface ISlipSummary {
    slip: ISlip;
    betSummaries: IBetSummary[];
    possibleWinnings: Decimal | null;
    rewardTokenModifications?: IRewardTokensModifications;
    totalStake: Decimal | null;
    totalOdds: CalculatedOdds;
    taxation?: ITaxationResult | null;
    betCount: number;
    hasXCastPick: boolean;
    hasStartingPrice: boolean;
    baseTotalOdds?: CalculatedOdds;
}

export interface IRewardTokensModifications {
    freeBet?: IFreeBetModification;
    oddsBoost?: IOddsBoostModification;
    riskFree?: IRiskFreeModification;
    accaBoost?: IAccaBoostModification;
}

export interface IFreeBetModification {
    amount: Decimal;
}

export interface IOddsBoostModification {
    boostedOdds: CalculatedOdds;
    boostedWinnings: Decimal;
}

export interface IRiskFreeModification {
    amount: Decimal;
}

export interface IAccaBoostModification {
    boostedWinnings: Decimal;
    originalWinnings: Decimal;
    boostRatio: Decimal;
    maxBoost: Decimal;
    currentBoost: Decimal;
}

export const emptySlipSummary: ISlipSummary = Object.freeze({
    slip: {
        slipId: '',
        bets: [],
        rewardToken: null,
        slipPicks: {},
        stake: null,
        type: new SingleSlipType(),
        hasError: false,
        index: 0,
    },
    betSummaries: [],
    possibleWinnings: null,
    taxation: null,
    totalStake: null,
    totalOdds: emptyCalculatedOdds,
    betCount: 0,
    hasXCastPick: false,
    hasStartingPrice: false,
});

export interface IBetslipSummary {
    slipSummaries: ISlipSummary[];
    /**
     * Total gross possible winnings in the betslip slip if user wins all slips.
     */
    possibleWinnings: Decimal | null;
    /**
     * Total net possible winnings after taxation and boosted values (reward tokens) applied
     */
    possibleWinningsNet: Decimal | null;
    totalStake: Decimal | null; // Total sum of money user needs to pay.
    taxation: ITaxationResult | null; // Taxation, if null then no taxation is not applied or not available.
    betCount: number; // How many bets user places in this betslip.
    hasBetBuilder: boolean; // If there is a betbuilder pick in betslip
    missingBetBuilderLegs: boolean; // If any of betbulider picks has below 2 legs
    totalOdds: CalculatedOdds; // Contains totalOdds, when betslip contains only one slip with only one bet with multiple legs.
    baseTotalOdds?: CalculatedOdds; // Contains base totalOdds of combo bet, when betslip is combo each way with multiple bets.
    teaserTotalOdds: Odds; // Contains totalOdds, when betslip contains only one slip with only one bet with multiple legs.
    averageOdds: CalculatedOdds; // Contains averageOdds, when betslip type is MultiSingle.
    isPotentailReturn: boolean;
}

export const emptyBetslipSummary: IBetslipSummary = Object.freeze({
    slipSummaries: [],
    possibleWinnings: null,
    possibleWinningsNet: null,
    totalStake: null,
    taxation: null,
    betCount: 0,
    legCount: 0,
    hasBetBuilder: false,
    missingBetBuilderLegs: false,
    totalOdds: emptyCalculatedOdds,
    teaserTotalOdds: emptyOdds,
    averageOdds: emptyCalculatedOdds,
    isPotentailReturn: false,
});
